import React, { useState, useEffect } from "react";
import {
  Sparkles,
  Users,
  Building2,
  ReceiptText,
  Wallet,
  Calendar,
  X,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertCircle,
  Clock,
  Loader2
} from "lucide-react";
import api from "../service/api";

export interface PoolCalculationResultProps {
  result: any;
  periodMonth?: number;
  periodYear?: number;
  firmId?: number;
  onClose?: () => void;
}

const monthNames = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

const formatCurrency = (val: number | undefined | null) => {
  if (val === undefined || val === null || isNaN(val)) return "₺0,00";
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
};

const formatDate = (dateVal: string | Date | undefined) => {
  if (!dateVal) return "";
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("tr-TR");
  } catch {
    return "";
  }
};

export const PoolCalculationResult: React.FC<PoolCalculationResultProps> = ({
  result,
  periodMonth,
  periodYear,
  firmId,
  onClose,
}) => {
  const [activeListTab, setActiveListTab] = useState<"all" | "users" | "accounts">("all");
  const [isDetailsExpanded, setIsDetailsExpanded] = useState<boolean>(true);
  const [accountNamesMap, setAccountNamesMap] = useState<Record<number, { name: string; externalId?: string }>>({});
  const [userNamesMap, setUserNamesMap] = useState<Record<number, { name: string; email?: string }>>({});
  const [isLoadingNames, setIsLoadingNames] = useState<boolean>(false);

  // Extract firm log data from various possible response formats
  let logData: any = result || {};
  if (result?.debugLog) {
    if (firmId && result.debugLog[firmId]) {
      logData = result.debugLog[firmId];
    } else {
      const keys = Object.keys(result.debugLog);
      if (keys.length > 0) {
        logData = result.debugLog[keys[0]];
      }
    }
  } else if (result?.data?.debugLog) {
    if (firmId && result.data.debugLog[firmId]) {
      logData = result.data.debugLog[firmId];
    } else {
      const keys = Object.keys(result.data.debugLog);
      if (keys.length > 0) {
        logData = result.data.debugLog[keys[0]];
      }
    }
  }

  // Plasiyer listesi ayrıştırma
  const userList: Array<{
    userId: number;
    userName: string;
    userEmail?: string;
    earnedAmount: number;
  }> = [];

  if (Array.isArray(logData?.userBreakdown)) {
    userList.push(...logData.userBreakdown);
  } else if (logData?.userCollections && typeof logData.userCollections === "object") {
    Object.entries(logData.userCollections).forEach(([uId, amount]) => {
      userList.push({
        userId: Number(uId),
        userName: `Plasiyer #${uId}`,
        earnedAmount: Number(amount) || 0,
      });
    });
  }

  // Bayi / Cari listesi ayrıştırma
  const accountList: Array<{
    accountId: number;
    accountName: string;
    externalId?: string;
    isDealer?: boolean;
    earnedAmount: number;
  }> = [];

  if (Array.isArray(logData?.accountBreakdown)) {
    accountList.push(...logData.accountBreakdown);
  } else if (logData?.accountCollections && typeof logData.accountCollections === "object") {
    Object.entries(logData.accountCollections).forEach(([accId, amount]) => {
      accountList.push({
        accountId: Number(accId),
        accountName: `Bayi / Cari #${accId}`,
        isDealer: true,
        earnedAmount: Number(amount) || 0,
      });
    });
  }

  // Cari ve Plasiyer isimlerini API'den çekme
  useEffect(() => {
    let isMounted = true;

    const fetchNames = async () => {
      const missingAccountIds = accountList
        .map((a) => a.accountId)
        .filter((id) => id && (!accountNamesMap[id] || accountNamesMap[id].name.startsWith("Bayi / Cari #")));

      const missingUserIds = userList
        .map((u) => u.userId)
        .filter((id) => id && (!userNamesMap[id] || userNamesMap[id].name.startsWith("Plasiyer #")));

      if (missingAccountIds.length === 0 && missingUserIds.length === 0) return;

      setIsLoadingNames(true);

      try {
        // 1. Carileri Sorgula
        if (missingAccountIds.length > 0) {
          const filter =
            missingAccountIds.length === 1
              ? { field: "id", operator: "eq", value: missingAccountIds[0] }
              : {
                  logic: "or",
                  filters: missingAccountIds.map((id) => ({ field: "id", operator: "eq", value: id })),
                };

          const accRes = await api.post(
            "/account/DynamicQuery",
            { filter },
            {
              params: { pageSize: Math.max(missingAccountIds.length, 50) },
              headers: { "x-silent": "true" },
            }
          );

          const rawAccounts = Array.isArray(accRes.data) ? accRes.data : accRes.data?.items || [];
          const newAccountMap: Record<number, { name: string; externalId?: string }> = {};

          rawAccounts.forEach((acc: any) => {
            if (acc.id) {
              newAccountMap[acc.id] = {
                name: acc.name || acc.title || `Cari #${acc.id}`,
                externalId: acc.externalId,
              };
            }
          });

          // Eğer DynamicQuery ile bulunamayan tekil ID varsa doğrudan GET /account/:id dene
          for (const accId of missingAccountIds) {
            if (!newAccountMap[accId]) {
              try {
                const singleRes = await api.get(`/account/${accId}`, { headers: { "x-silent": "true" } });
                if (singleRes.data?.name) {
                  newAccountMap[accId] = {
                    name: singleRes.data.name,
                    externalId: singleRes.data.externalId,
                  };
                }
              } catch {
                // Ignore fallback error
              }
            }
          }

          if (isMounted) {
            setAccountNamesMap((prev) => ({ ...prev, ...newAccountMap }));
          }
        }

        // 2. Plasiyerleri (Kullanıcıları) Sorgula
        if (missingUserIds.length > 0) {
          const userFilter =
            missingUserIds.length === 1
              ? { field: "id", operator: "eq", value: missingUserIds[0] }
              : {
                  logic: "or",
                  filters: missingUserIds.map((id) => ({ field: "id", operator: "eq", value: id })),
                };

          const userRes = await api.post(
            "/user/DynamicQuery",
            { filter: userFilter },
            {
              params: { pageSize: Math.max(missingUserIds.length, 50) },
              headers: { "x-silent": "true" },
            }
          );

          const rawUsers = Array.isArray(userRes.data) ? userRes.data : userRes.data?.items || [];
          const newUserMap: Record<number, { name: string; email?: string }> = {};

          rawUsers.forEach((u: any) => {
            if (u.id) {
              const fullName = u.name || `${u.firstName || ""} ${u.lastName || ""}`.trim();
              newUserMap[u.id] = {
                name: fullName || `Kullanıcı #${u.id}`,
                email: u.email,
              };
            }
          });

          // Fallback: tekil sorgulama
          for (const uId of missingUserIds) {
            if (!newUserMap[uId]) {
              try {
                const singleUserRes = await api.get(`/user/${uId}`, { headers: { "x-silent": "true" } });
                if (singleUserRes.data?.name) {
                  newUserMap[uId] = {
                    name: singleUserRes.data.name,
                    email: singleUserRes.data.email,
                  };
                }
              } catch {
                // Ignore fallback error
              }
            }
          }

          if (isMounted) {
            setUserNamesMap((prev) => ({ ...prev, ...newUserMap }));
          }
        }
      } catch (err) {
        console.warn("Cari / Plasiyer isimleri çözümlenirken hata:", err);
      } finally {
        if (isMounted) {
          setIsLoadingNames(false);
        }
      }
    };

    fetchNames();

    return () => {
      isMounted = false;
    };
  }, [result, accountList.length, userList.length]);

  // Toplamlar
  const totalUserEarned = userList.reduce((sum, u) => sum + (u.earnedAmount || 0), 0);
  const totalAccountEarned = accountList.reduce((sum, a) => sum + (a.earnedAmount || 0), 0);
  const totalEarned = logData?.summary?.totalEarned ?? (totalUserEarned + totalAccountEarned);
  const foundCollectionsCount = logData?.foundCollectionsInTargetMonth ?? logData?.summary?.foundCollectionsCount ?? 0;

  // Dönem ve Tarih Aralığı
  const monthName = periodMonth !== undefined ? monthNames[periodMonth] : "";
  const periodLabel = monthName && periodYear ? `${monthName} ${periodYear}` : (periodYear ? `${periodYear}` : "Seçilen Dönem");
  
  const startDateFormatted = formatDate(logData?.targetDateRange?.start);
  const endDateFormatted = formatDate(logData?.targetDateRange?.end);

  const hasData = foundCollectionsCount > 0 || totalEarned > 0 || userList.length > 0 || accountList.length > 0;

  if (!result) return null;

  return (
    <div className="w-full bg-card border border-borderColor rounded-xl shadow-md overflow-hidden transition-all duration-300 animate-modalIn mt-4">
      {/* Header Banner */}
      <div className="bg-bgAlt p-3.5 border-b border-borderColor flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <Sparkles size={18} className="animate-pulse" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-bold text-sm text-text">
                Havuz Hesaplama Sonucu: <span className="text-primary">{periodLabel}</span>
              </h4>
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-600 bg-green-500/10 border border-green-500/20 px-2 py-0.5 rounded-full">
                <CheckCircle2 size={12} /> Başarılı
              </span>
            </div>
            <p className="text-[11px] text-text opacity-60 flex items-center gap-2 mt-0.5">
              {startDateFormatted && endDateFormatted && (
                <span className="flex items-center gap-1">
                  <Calendar size={12} /> {startDateFormatted} - {endDateFormatted}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Clock size={12} /> {new Date().toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
              </span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
            className="p-1.5 rounded-lg text-text opacity-60 hover:opacity-100 hover:bg-bgAlt transition-colors"
            title={isDetailsExpanded ? "Detayları Gizle" : "Detayları Göster"}
          >
            {isDetailsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-text opacity-60 hover:opacity-100 hover:text-danger hover:bg-danger/10 transition-colors"
              title="Kapat"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Main Content Body */}
      <div className="p-4 space-y-4">
        {/* KPI Cards Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {/* Taranan Tahsilat */}
          <div className="p-3 rounded-xl bg-bgAlt border border-borderColor flex flex-col justify-between">
            <div className="flex items-center justify-between text-blue-500 mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-text opacity-60">Taranan Tahsilat</span>
              <ReceiptText size={16} />
            </div>
            <div className="text-lg font-black text-text">
              {foundCollectionsCount} <span className="text-xs font-normal opacity-60">Adet</span>
            </div>
          </div>

          {/* Plasiyer Primleri */}
          <div className="p-3 rounded-xl bg-bgAlt border border-borderColor flex flex-col justify-between">
            <div className="flex items-center justify-between text-purple-500 mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-text opacity-60">Plasiyer Primleri</span>
              <Users size={16} />
            </div>
            <div className="text-lg font-black text-text">
              {formatCurrency(totalUserEarned)}
            </div>
            <span className="text-[10px] text-text opacity-60">{userList.length} Plasiyer</span>
          </div>

          {/* Bayi Primleri */}
          <div className="p-3 rounded-xl bg-bgAlt border border-borderColor flex flex-col justify-between">
            <div className="flex items-center justify-between text-indigo-500 mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-text opacity-60">Bayi Primleri</span>
              <Building2 size={16} />
            </div>
            <div className="text-lg font-black text-text">
              {formatCurrency(totalAccountEarned)}
            </div>
            <span className="text-[10px] text-text opacity-60">{accountList.length} Bayi</span>
          </div>

          {/* Toplam Dağıtılan */}
          <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/30 flex flex-col justify-between">
            <div className="flex items-center justify-between text-green-600 dark:text-green-400 mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">Toplam Dağıtılan</span>
              <Wallet size={16} />
            </div>
            <div className="text-lg font-black text-green-700 dark:text-green-300">
              {formatCurrency(totalEarned)}
            </div>
            <span className="text-[10px] opacity-70">Havuz Bakiyesi</span>
          </div>
        </div>

        {/* Empty State Warning */}
        {!hasData && (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-text">
            <AlertCircle size={18} className="text-yellow-600 dark:text-yellow-400 shrink-0" />
            <div className="text-xs">
              <span className="font-bold">{periodLabel}</span> dönemine ait kriterlere uygun tahsilat hareketi (COLLECTION_IN) bulunamadı. Havuz bakiyelerine herhangi bir tutar eklenmedi.
            </div>
          </div>
        )}

        {/* Detailed Breakdown Section (Collapsible) */}
        {isDetailsExpanded && (userList.length > 0 || accountList.length > 0) && (
          <div className="space-y-3 pt-2 border-t border-borderColor">
            {/* Filter Tabs */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setActiveListTab("all")}
                className={`text-xs px-2.5 py-1 rounded-lg font-semibold transition-colors ${
                  activeListTab === "all"
                    ? "bg-primary text-white"
                    : "bg-bgAlt text-text opacity-70 hover:opacity-100"
                }`}
              >
                Tümü ({userList.length + accountList.length})
              </button>
              {userList.length > 0 && (
                <button
                  type="button"
                  onClick={() => setActiveListTab("users")}
                  className={`text-xs px-2.5 py-1 rounded-lg font-semibold transition-colors ${
                    activeListTab === "users"
                      ? "bg-primary text-white"
                      : "bg-bgAlt text-text opacity-70 hover:opacity-100"
                  }`}
                >
                  Plasiyerler ({userList.length})
                </button>
              )}
              {accountList.length > 0 && (
                <button
                  type="button"
                  onClick={() => setActiveListTab("accounts")}
                  className={`text-xs px-2.5 py-1 rounded-lg font-semibold transition-colors ${
                    activeListTab === "accounts"
                      ? "bg-primary text-white"
                      : "bg-bgAlt text-text opacity-70 hover:opacity-100"
                  }`}
                >
                  Bayiler ({accountList.length})
                </button>
              )}
            </div>

            {/* List Table / Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
              {/* Plasiyer Items */}
              {(activeListTab === "all" || activeListTab === "users") &&
                userList.map((user) => {
                  const resolvedUser = userNamesMap[user.userId];
                  const displayName = resolvedUser?.name && !resolvedUser.name.startsWith("Kullanıcı #") 
                    ? resolvedUser.name 
                    : (user.userName && !user.userName.startsWith("Plasiyer #") ? user.userName : `Plasiyer #${user.userId}`);
                  const displaySub = resolvedUser?.email || user.userEmail || `Plasiyer #${user.userId}`;

                  return (
                    <div
                      key={`user-${user.userId}`}
                      className="flex items-center justify-between p-2.5 rounded-lg bg-bgAlt border border-borderColor text-xs hover:border-primary/50 transition-colors"
                    >
                      <div className="flex items-center gap-2.5 min-w-0 pr-2">
                        <div className="p-1.5 rounded-md bg-purple-500/10 text-purple-600 dark:text-purple-400 shrink-0">
                          <Users size={14} />
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold text-text truncate" title={displayName}>
                            {displayName}
                          </div>
                          <div className="text-[10px] text-text opacity-60 truncate" title={displaySub}>
                            {displaySub}
                          </div>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-extrabold text-green-600 dark:text-green-400">
                          +{formatCurrency(user.earnedAmount)}
                        </div>
                        <div className="text-[9px] text-text opacity-50">Plasiyer Havuzu</div>
                      </div>
                    </div>
                  );
                })}

              {/* Bayi Items */}
              {(activeListTab === "all" || activeListTab === "accounts") &&
                accountList.map((account) => {
                  const resolvedAcc = accountNamesMap[account.accountId];
                  const displayName = resolvedAcc?.name && !resolvedAcc.name.startsWith("Cari #")
                    ? resolvedAcc.name
                    : (account.accountName && !account.accountName.startsWith("Bayi / Cari #") ? account.accountName : `Bayi / Cari #${account.accountId}`);
                  const displayCode = resolvedAcc?.externalId || account.externalId;

                  return (
                    <div
                      key={`account-${account.accountId}`}
                      className="flex items-center justify-between p-2.5 rounded-lg bg-bgAlt border border-borderColor text-xs hover:border-primary/50 transition-colors"
                    >
                      <div className="flex items-center gap-2.5 min-w-0 pr-2">
                        <div className="p-1.5 rounded-md bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 shrink-0">
                          <Building2 size={14} />
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold text-text truncate" title={displayName}>
                            {displayName}
                          </div>
                          <div className="text-[10px] text-text opacity-60 truncate">
                            {displayCode ? `Kod: ${displayCode}` : `Cari #${account.accountId}`}
                          </div>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-extrabold text-green-600 dark:text-green-400">
                          +{formatCurrency(account.earnedAmount)}
                        </div>
                        <div className="text-[9px] text-text opacity-50">Bayi Havuzu</div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
